import React from "react";
import { Lock } from "lucide-react";
import Skeleton from "../common/Skeleton";
import NoDataFound from "../common/NoDataFound";
import ApprovalDot, { WorkflowState } from "../common/ApprovalDot";

export interface PositionBadge {
  position_name: string;
  position_code?: string;
}

interface DisplayUser {
  id: number;
  name: string;
  email: string;
  phone: string;
  employeeId: string;
  status: "Active" | "Inactive";
  isApproved: boolean;
  workflow_state?: WorkflowState;
  roles: string[];
  /** Count of active roles held in this entity (list-level aggregate). */
  roleCount?: number;
  avatar: string;
  positions?: PositionBadge[];
  is_entity_admin?: number;
}

interface UserListProps {
  users: DisplayUser[];
  loading: boolean;
  onEdit: (user: DisplayUser) => void;
  onDotClick: (user: DisplayUser) => void;
  /** Current 1-based page — used to compute the running S.No. */
  page?: number;
  /** Rows per page — used to compute the running S.No. */
  pageSize?: number;
}

const UserRow: React.FC<{
  user: DisplayUser;
  serial: number;
  onEdit: () => void;
  onDotClick: () => void;
}> = ({ user, serial, onEdit, onDotClick }) => {
  const isEntityAdmin = !!user.is_entity_admin;

  return (
    <tr
      className={`table-list-row${user.status !== "Active" ? " is-inactive" : ""}`}
      onClick={onEdit}
      data-testid={`BTN_user_list_row_${user.id}_open`}
    >
      <td
        className="table-list-cell table-list-cell--dot"
        onClick={(e) => { e.stopPropagation(); onDotClick(); }}
        title="Workflow — view timeline & act"
        data-testid={`BTN_user_list_row_${user.id}_workflow`}
      >
        <ApprovalDot
          state={user.workflow_state || "draft"}
          onClick={() => onDotClick()}
        />
      </td>
      <td className="table-list-cell table-list-cell--sno">{serial}</td>
      <td className="table-list-cell">
        <span className="table-list-cell-title cell-name-lock">
          {(isEntityAdmin || user.workflow_state === "approved") && (
            <Lock
              className="lock-icon lock-icon--sm"
              aria-label={isEntityAdmin ? "Entity Admin" : "Locked — approved"}
            />
          )}
          {user.name}
        </span>
        <span className="table-list-cell-sub">{user.email || "—"}</span>
      </td>
      <td className="table-list-cell">{user.employeeId || "—"}</td>
      <td className="table-list-cell">
        {(() => {
          const count = user.roleCount ?? user.roles.length;
          return count > 0 ? (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
              title={`${count} role${count === 1 ? "" : "s"} assigned`}
              data-testid={`LBL_user_list_row_${user.id}_role_count`}
            >
              {count} {count === 1 ? "role" : "roles"}
            </span>
          ) : (
            <span className="text-gray-400" data-testid={`LBL_user_list_row_${user.id}_role_count`}>—</span>
          );
        })()}
      </td>
    </tr>
  );
};

const UserList: React.FC<UserListProps> = ({ users, loading, onEdit, onDotClick, page = 1, pageSize = users.length }) => {
  return (
    <div className="table-list">
      <div className="table-list-scroll">
        <table className="table-list-table">
          <thead>
            <tr>
              <th className="table-list-th table-list-cell--dot" aria-label="Workflow status" />
              <th className="table-list-th table-list-cell--sno">S.No</th>
              <th className="table-list-th">Name</th>
              <th className="table-list-th">Login ID</th>
              <th className="table-list-th">Roles</th>
            </tr>
          </thead>
          <tbody aria-busy={loading} aria-live="polite">
            {loading && (
              <Skeleton.TableRows cols={["icon", "sm", "md", "sm", "md"]} />
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="table-list-cell">
                  <NoDataFound
                    title="No users found"
                    message='Click "Create User" to add one.'
                  />
                </td>
              </tr>
            )}
            {!loading && users.map((user, idx) => (
              <UserRow
                key={user.id}
                user={user}
                serial={(page - 1) * pageSize + idx + 1}
                onEdit={() => onEdit(user)}
                onDotClick={() => onDotClick(user)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserList;
